import type React from 'react';
import { getStylePackById } from '@/themes/stylePacks';
import type { StylePackType } from '@/themes/stylePack.types';

/**
 * Типографика заголовка и подзаголовка из стиль-пака — повтор эталона Slimkit,
 * spec shared/typography.md «Что добавить» п.2 (заголовок 32/38, подзаголовок
 * 18/24 с трекингом 0.035 px). Переменные ставит themes/stylePackExtraVars.ts:
 * `type.h1LineHeight` → `--h1-line-height`, `type.subLineHeight` /
 * `type.subTracking` → `--sub-line-height` / `--sub-tracking`.
 *
 * Главное правило: у пака БЕЗ этих полей (12 прежних паков, воронки без пака)
 * разметка блока остаётся байт-в-байт прежней — это держит эталон
 * PublicBlockRenderer.snapshot.web2921. Поэтому ссылка на переменную
 * появляется в inline-стиле только тогда, когда пак поле задал; иначе —
 * прежнее значение (у заголовка число 1.15, у подзаголовка — ничего, оба
 * свойства наследуются от предков, как было до правки).
 */

function packType(stylePackId: string | undefined): StylePackType | undefined {
  return stylePackId ? getStylePackById(stylePackId)?.type : undefined;
}

/** Прежний интерлиньяж заголовка `title` — множитель, без единиц. */
export const LEGACY_TITLE_LINE_HEIGHT = 1.15;

/**
 * Интерлиньяж заголовка `title`. Пак задал интерлиньяж (на телефоне или только
 * на десктопе — правило ≥566 px переставляет `--h1-line-height` на двойник) —
 * ссылка на переменную с прежним запасным значением; не задал — прежнее число.
 */
export function titleLineHeight(
  stylePackId: string | undefined,
): number | string {
  const type = packType(stylePackId);
  const packSetsIt =
    typeof type?.h1LineHeight === 'number' ||
    typeof type?.desktop?.h1LineHeight === 'number';
  return packSetsIt
    ? `var(--h1-line-height, ${LEGACY_TITLE_LINE_HEIGHT})`
    : LEGACY_TITLE_LINE_HEIGHT;
}

/**
 * Интерлиньяж и трекинг подзаголовка `sub_title`. Ссылки — без запасного
 * значения, и только на те поля, что пак задал: до этой правки у подзаголовка не
 * было ни `line-height`, ни `letter-spacing`, оба наследовались, и у пака без
 * полей свойства в inline-стиль не попадают вовсе.
 */
export function subTitleTypeStyle(
  stylePackId: string | undefined,
): Pick<React.CSSProperties, 'lineHeight' | 'letterSpacing'> {
  const type = packType(stylePackId);
  const style: Pick<React.CSSProperties, 'lineHeight' | 'letterSpacing'> = {};
  if (typeof type?.subLineHeight === 'number') {
    style.lineHeight = 'var(--sub-line-height)';
  }
  if (typeof type?.subTracking === 'number') {
    style.letterSpacing = 'var(--sub-tracking)';
  }
  return style;
}
